// Aperçu du livrable : le rapport assemblé, à partir des données RÉELLES.
//
// Chaque section relit la base au moment du rendu — c'est le « rapport
// dynamique ». Rien n'est figé côté livrable : imprimer (Ctrl+P) fige au moment
// où on imprime, et c'est le seul instantané. Cette section n'invente aucun
// chiffre : elle réutilise exactement ce que le tableau de bord affiche.
import {
  useAdoption,
  useDecisions,
  useInsights,
  useMetrics,
  usePersonas,
  useTests,
  useTriangulations,
} from '../api/queries';
import type { SectionKey } from '../api/types';
import { orderedSections } from './sections';
import { HYPOTHESIS_STATUS_LABEL, OUTCOME_LABEL } from '../insights/labels';
import { initialsOf } from '../components/AppShell';
import { personaAside, personaTitle } from '../personas/labels';

function Absent({ children }: { children: string }) {
  return <p className="report-empty">{children}</p>;
}

// --- Sections ---------------------------------------------------------------

function SyntheseSection({ projectId }: { projectId: string }) {
  const metrics = useMetrics(projectId);
  if (metrics.isPending) return <Absent>Chargement…</Absent>;
  if (metrics.isError) return <Absent>Indicateurs indisponibles.</Absent>;

  // On retient les indicateurs les plus parlants pour un commanditaire.
  const m = metrics.data.metrics;
  const pick = [
    m.evidence.find((x) => x.key === 'facts'),
    m.evidence.find((x) => x.key === 'sources'),
    m.traceability.find((x) => x.key === 'insights_supported'),
    m.coverage.find((x) => x.key === 'personas'),
  ].filter((x): x is NonNullable<typeof x> => Boolean(x));

  return (
    <div className="report-tiles">
      {pick.map((x) => (
        <div key={x.key} className="report-tile">
          <span className="report-tile-value">
            {x.value === null ? '—' : x.value}
            {x.value !== null && x.unit ? <span className="report-tile-unit">{x.unit}</span> : null}
          </span>
          <span className="report-tile-label">{x.label}</span>
        </div>
      ))}
    </div>
  );
}

function AdoptionSection({ projectId }: { projectId: string }) {
  const adoption = useAdoption(projectId);
  if (adoption.isPending) return <Absent>Chargement…</Absent>;
  if (adoption.isError) return <Absent>Adoption indisponible.</Absent>;

  const { global, pillars } = adoption.data;
  const scored = pillars.filter((p) => p.score !== null);

  if (global.val === null) {
    return <Absent>Le tableau d’adoption n’a pas encore été construit pour ce projet.</Absent>;
  }

  return (
    <div>
      <div className="report-score">
        <span className="report-score-value">
          {global.val}
          <span className="report-score-unit">/100</span>
        </span>
        {global.isCapped ? <span className="report-score-cap">plafonné · signal critique</span> : null}
        {global.confidence !== null ? (
          <span className="report-score-conf">confiance {global.confidence}%</span>
        ) : null}
      </div>
      <table className="zy-table zy-table--dense report-table">
        <thead>
          <tr>
            <th>Pilier</th>
            <th className="num">Note</th>
            <th className="num">Tendance</th>
          </tr>
        </thead>
        <tbody>
          {scored.map((p) => (
            <tr key={p.pillarId}>
              <td>
                {p.name}
                {p.isCapped ? <span className="report-flag">plafond</span> : null}
              </td>
              <td className="num">{p.score?.toFixed(1)}</td>
              <td className="num">
                {p.delta === null ? '—' : `${p.delta > 0 ? '+' : ''}${p.delta}`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InsightsSection({ projectId }: { projectId: string }) {
  const insights = useInsights(projectId);
  if (insights.isPending) return <Absent>Chargement…</Absent>;
  if (insights.isError) return <Absent>Insights indisponibles.</Absent>;
  if (insights.data.length === 0) return <Absent>Aucun insight sur ce projet.</Absent>;

  return (
    <ul className="report-insights">
      {insights.data.map((i) => (
        <li key={i.id}>
          <div className="report-insight-head">
            <span className="report-insight-title">{i.title}</span>
            {i.severity ? <span className="report-flag">{i.severity}</span> : null}
          </div>
          {i.summary ? <p className="report-insight-summary">{i.summary}</p> : null}
          <span className={`report-evidence${i.evidence_count === 0 ? ' report-evidence-none' : ''}`}>
            {i.evidence_count === 0
              ? 'Aucun fait cité — non étayé'
              : `${i.evidence_count} fait${i.evidence_count > 1 ? 's' : ''} cité${i.evidence_count > 1 ? 's' : ''}`}
          </span>
        </li>
      ))}
    </ul>
  );
}

function PersonasSection({ projectId }: { projectId: string }) {
  const personas = usePersonas(projectId);
  if (personas.isPending) return <Absent>Chargement…</Absent>;
  if (personas.isError) return <Absent>Personas indisponibles.</Absent>;
  if (personas.data.length === 0) return <Absent>Aucun persona décrit.</Absent>;

  return (
    <div className="report-personas">
      {personas.data.map((p) => (
        <div key={p.id} className="report-persona">
          <span
            className="report-persona-avatar"
            style={p.accent ? { background: p.accent } : undefined}
            aria-hidden="true"
          >
            {p.initials ?? initialsOf(p.name)}
          </span>
          <div>
            <span className="report-persona-name">{personaTitle(p)}</span>
            <span className="report-persona-role">
              {[personaAside(p) ?? 'Rôle non renseigné', p.pole].filter(Boolean).join(' · ')}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

function TriangulationSection({ projectId }: { projectId: string }) {
  const tris = useTriangulations(projectId);
  if (tris.isPending) return <Absent>Chargement…</Absent>;
  if (tris.isError) return <Absent>Triangulation indisponible.</Absent>;
  const rows = tris.data.flatMap((t) => t.rows);
  if (rows.length === 0) return <Absent>Aucune mesure de triangulation.</Absent>;

  return (
    <table className="zy-table zy-table--dense report-table">
      <thead>
        <tr>
          <th>Pilier</th>
          <th className="num">Note</th>
          <th>Donnée mesurée</th>
          <th>Concordance</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id}>
            <td>{r.pillar_name ?? r.pillar_label ?? '—'}</td>
            <td className="num">{r.score ?? '—'}</td>
            <td>{r.quant_summary ?? '—'}</td>
            <td>{r.concordance ?? '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function TestsSection({ projectId }: { projectId: string }) {
  const tests = useTests(projectId);
  if (tests.isPending) return <Absent>Chargement…</Absent>;
  if (tests.isError) return <Absent>Tests indisponibles.</Absent>;
  if (tests.data.length === 0) return <Absent>Aucun test utilisateur.</Absent>;

  return (
    <table className="zy-table zy-table--dense report-table">
      <thead>
        <tr>
          <th>Test</th>
          <th className="num">Participants</th>
          <th className="num">Réussite mesurée</th>
        </tr>
      </thead>
      <tbody>
        {tests.data.map((t) => (
          <tr key={t.id}>
            <td>{t.objective ?? t.code ?? t.id}</td>
            <td className="num">{t.participants}</td>
            {/* Le taux MESURÉ (moyenne des fonctionnalités relevées), pas le
                chiffre saisi à la main : un livrable ne doit pas annoncer une
                réussite que rien n'établit. */}
            <td className="num">
              {t.measured_score === null
                ? '—'
                : `${Math.round(t.measured_score)}% (${t.measured_count})`}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Le journal des décisions (migration 051) : chaque verdict avec ce qui l'a fondé. */
function DecisionsSection({ projectId }: { projectId: string }) {
  const decisions = useDecisions(projectId);
  if (decisions.isPending) return <Absent>Chargement…</Absent>;
  if (decisions.isError) return <Absent>Décisions indisponibles.</Absent>;
  if (decisions.data.length === 0) return <Absent>Aucune décision sur ce projet.</Absent>;

  const n = (v: number) => String(v).replace('.', ',');
  return (
    <ul className="report-insights">
      {decisions.data.map((d) => (
        <li key={d.id}>
          <div className="report-insight-head">
            <span className="report-insight-title">{d.insight_title}</span>
            <span className="report-flag">{HYPOTHESIS_STATUS_LABEL[d.status_to]}</span>
          </div>
          {d.hypothesis ? <p className="report-insight-summary">{d.hypothesis}</p> : null}
          {d.note ? <p className="report-insight-summary">Raison : {d.note}</p> : null}
          <span className="report-evidence">
            {new Date(d.decided_at).toLocaleDateString('fr-FR')}
            {d.decided_by_name ? ` · ${d.decided_by_name}` : ''}
            {d.metric?.outcome
              ? ` · ${d.metric.label} : ${n(d.metric.outcome.baseline)} → ${n(d.metric.outcome.current)} (cible ${n(d.metric.outcome.target)}), ${OUTCOME_LABEL[d.metric.outcome.level].toLowerCase()}`
              : ''}
            {d.tests.length > 0
              ? ` · ${d.tests.map((t) => `${t.code ?? t.objective ?? t.id} ${t.success_rate !== null ? `${n(Number(t.success_rate))} %` : '—'}`).join(', ')}`
              : ''}
          </span>
        </li>
      ))}
    </ul>
  );
}

function ReportSection({ section, projectId }: { section: SectionKey; projectId: string }) {
  switch (section) {
    case 'synthese':
      return <SyntheseSection projectId={projectId} />;
    case 'adoption':
      return <AdoptionSection projectId={projectId} />;
    case 'insights':
      return <InsightsSection projectId={projectId} />;
    case 'personas':
      return <PersonasSection projectId={projectId} />;
    case 'triangulation':
      return <TriangulationSection projectId={projectId} />;
    case 'tests':
      return <TestsSection projectId={projectId} />;
    case 'decisions':
      return <DecisionsSection projectId={projectId} />;
    default:
      return null;
  }
}

export function Report({
  projectId,
  projectName,
  title,
  sections,
}: {
  projectId: string;
  projectName: string;
  title: string;
  sections: SectionKey[];
}) {
  const ordered = orderedSections(sections);
  const printedAt = new Date().toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <article className="report">
      <header className="report-head">
        <span className="t-mono">{projectName}</span>
        <h1 className="report-title">{title || 'Restitution sans titre'}</h1>
        <p className="report-date">Rapport dynamique — état au {printedAt}</p>
      </header>

      {ordered.length === 0 ? (
        <Absent>Aucune section sélectionnée. Cochez ce que le rapport doit montrer.</Absent>
      ) : (
        ordered.map((s) => (
          <section key={s.key} className="report-section">
            <h2 className="report-section-title">{s.label}</h2>
            <ReportSection section={s.key} projectId={projectId} />
          </section>
        ))
      )}
    </article>
  );
}
